import {
	Button,
	DataTable,
	type DataTableColumn,
	EmptyState,
	PageHeader,
	Stack,
	Text,
	Toolbar,
} from "@app/ui"
import { Tag } from "@app/ui/next"
import { useQuery } from "@tanstack/react-query"
import { Link, createRoute, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import {
	type MerchantChangeClassification,
	type MerchantChangeListItem,
	heistsQueryKeys,
	listHeists,
} from "../../api/heists"
import { formatDate, humanizeStatus } from "../../lib/format"
import { HttpError } from "../../lib/http"
import { useScope } from "../../scope/scope-context"
import { Route as RootRoute } from "../__root"

/**
 * Heists tenant-wide list -- `/heists`.
 *
 * This screen exists because the dashboard's "Heists pending review" card
 * pointed at `/shipments`: you clicked a count of flagged party swaps and
 * landed on an unfiltered shipment list with no indication of which rows were
 * flagged. There was no heist list to send it to — the only way to reach a
 * merchant change was to already know its UUID.
 *
 * Scope comes from `useScope()` and is forwarded to the engine, which filters
 * through `shipments` exactly as `dashboard.service.ts` counts. That symmetry
 * is the point -- if the card says 2 and this list shows 5, the fix is worse
 * than the bug it replaced.
 *
 * Mirrors `/lots`: PageHeader + Toolbar + DataTable with the canonical
 * five-state scaffold.
 */
export const Route = createRoute({
	getParentRoute: () => RootRoute,
	path: "/heists",
	component: HeistsIndexPage,
})

const SUBTITLE =
	"Party swaps flagged at a structural slot. Classify each one before downstream cascades fire."

/**
 * PRD §7: a correction is a field change within the same Party.id; a heist is
 * a Party.id SWAP at a structural slot. Only PENDING_REVIEW is actionable, so
 * it leads and is the default filter target from the dashboard.
 */
const FILTERS: ReadonlyArray<{ value: MerchantChangeClassification | "ALL"; label: string }> = [
	{ value: "PENDING_REVIEW", label: "Pending review" },
	{ value: "HEIST", label: "Heist" },
	{ value: "TREATED_AS_CORRECTION", label: "Correction" },
	{ value: "DISMISSED", label: "Dismissed" },
	{ value: "ALL", label: "All" },
]

const classificationColor = (c: MerchantChangeClassification): "harbor" | "neutral" =>
	c === "HEIST" ? "harbor" : "neutral"

export function HeistsIndexPage() {
	const navigate = useNavigate()
	const { params: scope } = useScope()
	// Pending review is the only actionable state, and it is what the dashboard
	// card counts — so landing from that card shows exactly those rows.
	const [filter, setFilter] = useState<MerchantChangeClassification | "ALL">("PENDING_REVIEW")
	const classification = filter === "ALL" ? undefined : filter

	const query = useQuery({
		queryKey: heistsQueryKeys.list(scope, classification),
		queryFn: ({ signal }) =>
			listHeists({ scope, ...(classification ? { classification } : {}), signal }),
	})

	const rows = query.data ?? []
	const countLabel = query.isSuccess
		? `${rows.length} ${rows.length === 1 ? "change" : "changes"}`
		: "Merchant changes"

	const columns: DataTableColumn<MerchantChangeListItem>[] = [
		{
			key: "shipment",
			header: "Shipment",
			mono: true,
			cell: (h) => (
				<Link
					to="/shipments/$shipmentId"
					params={{ shipmentId: h.shipmentId }}
					onClick={(e) => e.stopPropagation()}
				>
					{h.shipmentRef}
				</Link>
			),
		},
		{
			key: "slot",
			header: "Slot",
			cell: (h) => <Tag>{h.partyRole.replace(/_/g, " ")}</Tag>,
		},
		{
			// The whole event in one cell: who was replaced by whom. Names, never
			// the party UUIDs — the engine resolves them.
			key: "swap",
			header: "Swap",
			cell: (h) => `${h.previousPartyName ?? "—"} → ${h.newPartyName ?? "—"}`,
		},
		{
			// `detectionLayer` stays raw on purpose: L2_DBL_CLASSIFIER is the
			// layer's real name in PRD §7, not a label to prettify.
			key: "detected",
			header: "Detected",
			mono: true,
			cell: (h) => `${h.detectionLayer} · ${formatDate(h.detectedAt)}`,
		},
		{
			key: "classification",
			header: "Classification",
			align: "end",
			cell: (h) => (
				<Tag color={classificationColor(h.classification)}>{humanizeStatus(h.classification)}</Tag>
			),
		},
	]

	return (
		<Stack gap="5" align="stretch">
			<PageHeader title="Merchant changes" subtitle={SUBTITLE} />

			<Toolbar lead={<Text variant="label">{countLabel}</Text>}>
				{FILTERS.map((f) => (
					<Button
						key={f.value}
						variant={filter === f.value ? "primary" : "secondary"}
						size="sm"
						onClick={() => setFilter(f.value)}
					>
						{f.label}
					</Button>
				))}
			</Toolbar>

			{query.isError ? (
				<ErrorState error={query.error} onRetry={() => query.refetch()} />
			) : (
				<DataTable
					columns={columns}
					rows={rows}
					getRowKey={(h) => h.id}
					isLoading={query.isPending}
					onRowClick={(h) => navigate({ to: "/heists/$heistId", params: { heistId: h.id } })}
					empty={<EmptyStateBody filter={filter} />}
				/>
			)}
		</Stack>
	)
}

function ErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
	const message =
		error instanceof HttpError
			? `HTTP ${error.status}`
			: error instanceof Error
				? error.message
				: "Unknown error"
	return (
		<Stack gap="3" align="start">
			<Text variant="heading-sm" tone="danger">
				Couldn't load merchant changes
			</Text>
			<Text variant="body-sm" tone="muted">
				{message}
			</Text>
			<Button variant="secondary" size="sm" onClick={onRetry}>
				Retry
			</Button>
		</Stack>
	)
}

function EmptyStateBody({ filter }: { filter: MerchantChangeClassification | "ALL" }) {
	// "Nothing pending" is good news and should read that way; an empty
	// non-default filter is just an empty filter.
	if (filter === "PENDING_REVIEW") {
		return (
			<EmptyState
				title="Nothing pending review"
				description="Every flagged party swap has been classified."
			/>
		)
	}
	return (
		<EmptyState
			title="No merchant changes"
			description="Nothing matches this classification in the current scope."
		/>
	)
}
